import React, { useEffect, useState, useMemo, useRef } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

const ETL_JOBS_QUERY = gql`
  query EtlJobs($limit: Int) {
    etlJobs(limit: $limit) {
      id
      blobName
      originalFileName
      fileType
      status
      uploadedBy
      uploadedAt
      startedAt
      completedAt
      totalRows
      processedRows
      successCount
      failureCount
      errorMessage
      logBlobName
    }
  }
`;

function formatDuration(startedAt, completedAt) {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffMs = end - start;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDate(isoString) {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleString();
}

function StatusBadge({ status }) {
  return (
    <span className={`etl-status-badge etl-status-badge--${status}`}>
      {status}
    </span>
  );
}

export default function EtlJobHistory({ refreshKey, onResumePolling, onDownloadLog, onDownloadFile }) {
  const { data, loading, error, refetch } = useQuery(ETL_JOBS_QUERY, {
    variables: { limit: 25 },
    fetchPolicy: 'network-only'
  });
  const [fileNameFilter, setFileNameFilter] = useState('');
  const resumedJobIdRef = useRef(null);

  // Refetch when refreshKey changes (e.g., after a job completes)
  useEffect(() => {
    if (refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  // Resume polling for the most recent running/queued job — but only once per
  // unique jobId. The parent's onResumePolling callback is a fresh closure on
  // every render, so depending on it directly created an infinite re-fire loop.
  useEffect(() => {
    if (!data?.etlJobs || !onResumePolling) return;
    const runningJob = data.etlJobs.find(j => j.status === 'running' || j.status === 'queued');
    if (runningJob && runningJob.id !== resumedJobIdRef.current) {
      resumedJobIdRef.current = runningJob.id;
      onResumePolling(runningJob.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const jobs = data?.etlJobs || [];

  const displayName = (job) => job.originalFileName || (job.blobName || '').split('/').pop() || job.blobName || '';

  const filteredJobs = useMemo(() => {
    const q = fileNameFilter.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(j => displayName(j).toLowerCase().includes(q));
  }, [jobs, fileNameFilter]);

  if (loading) return <div className="etl-history-loading">Loading job history...</div>;
  if (error) return <div className="etl-error-message">Failed to load job history: {error.message}</div>;

  return (
    <div className="etl-job-history">
      <div className="etl-job-history-header">
        <h3>Job History</h3>
        <input
          type="text"
          className="etl-history-filter"
          placeholder="Filter by file name..."
          value={fileNameFilter}
          onChange={(e) => setFileNameFilter(e.target.value)}
        />
      </div>

      {jobs.length === 0 ? (
        <p className="etl-no-jobs">No import jobs yet.</p>
      ) : filteredJobs.length === 0 ? (
        <p className="etl-no-jobs">No jobs match &ldquo;{fileNameFilter}&rdquo;.</p>
      ) : (
        <table className="etl-history-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Type</th>
              <th>Status</th>
              <th>Rows</th>
              <th>Success</th>
              <th>Failed</th>
              <th>Submitted By</th>
              <th>Started</th>
              <th>Duration</th>
              <th>Source</th>
              <th>Log</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map(job => (
              <tr key={job.id} className={`etl-history-row etl-history-row--${job.status}`}>
                <td className="etl-history-filename" title={displayName(job)}>{displayName(job)}</td>
                <td>{job.fileType}</td>
                <td><StatusBadge status={job.status} /></td>
                <td>{job.totalRows?.toLocaleString() ?? '-'}</td>
                <td>{job.successCount?.toLocaleString() ?? '-'}</td>
                <td>{job.failureCount?.toLocaleString() ?? '-'}</td>
                <td>{job.uploadedBy}</td>
                <td>{formatDate(job.uploadedAt)}</td>
                <td>{formatDuration(job.startedAt, job.completedAt)}</td>
                <td>
                  {job.blobName && onDownloadFile ? (
                    <button
                      className="etl-download-log-btn"
                      onClick={() => onDownloadFile(job.blobName)}
                    >
                      Download
                    </button>
                  ) : '-'}
                </td>
                <td>
                  {job.logBlobName && onDownloadLog ? (
                    <button
                      className="etl-download-log-btn"
                      onClick={() => onDownloadLog(job.logBlobName)}
                    >
                      Download
                    </button>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
